import { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, List } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Lightweight rich text editor supporting bold, italic, and bullet lists.
 * Stores content as HTML. No external dependencies.
 */
export function RichTextEditor({ value = "", onChange, placeholder = "Enter text...", className = "" }) {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);
  const lastValue = useRef(value);

  // Set initial content ONCE on mount (never re-render from parent)
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || "";
      isInitialized.current = true;
    }
  }, []);

  // Only update from parent if value changed externally (e.g., different game selected)
  useEffect(() => {
    if (isInitialized.current && editorRef.current && value !== lastValue.current) {
      const currentHtml = editorRef.current.innerHTML;
      if (value !== currentHtml) {
        editorRef.current.innerHTML = value || "";
        lastValue.current = value;
      }
    }
  }, [value]);

  const execCmd = useCallback((command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    // Read back and notify parent
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.textContent?.trim() || "";
    lastValue.current = text ? html : "";
    onChange?.(lastValue.current);
  }, [onChange]);

  const handleInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.textContent?.trim() || "";
    lastValue.current = text ? html : "";
    onChange?.(lastValue.current);
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }, []);

  const isEmpty = !value;

  return (
    <div className={`border border-white/20 rounded-md overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-white/5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-white/10"
          onMouseDown={(e) => { e.preventDefault(); execCmd("bold"); }}
          title="Bold"
          data-testid="rte-bold-btn"
        >
          <Bold className="w-3.5 h-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-white/10"
          onMouseDown={(e) => { e.preventDefault(); execCmd("italic"); }}
          title="Italic"
          data-testid="rte-italic-btn"
        >
          <Italic className="w-3.5 h-3.5" />
        </Button>
        <div className="w-px h-4 bg-white/10 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-white/10"
          onMouseDown={(e) => { e.preventDefault(); execCmd("insertUnorderedList"); }}
          title="Bullet List"
          data-testid="rte-bullet-btn"
        >
          <List className="w-3.5 h-3.5" />
        </Button>
      </div>
      {/* Editor */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-3 left-3 text-white/30 pointer-events-none text-sm">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          className="min-h-[120px] max-h-[300px] overflow-y-auto p-3 text-sm text-white/90 outline-none bg-transparent
            [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1"
          onInput={handleInput}
          onPaste={handlePaste}
          suppressContentEditableWarning
          data-testid="rte-editor"
        />
      </div>
    </div>
  );
}

/**
 * Read-only renderer for rich text content stored as HTML.
 * Sanitizes to only allow safe tags.
 */
export function RichTextDisplay({ content, className = "" }) {
  if (!content) return null;

  // Basic sanitization - allow only safe tags
  const sanitized = content
    .replace(/<script[^>]*>.*?<\/script>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "");

  return (
    <div
      className={`text-sm text-white/80 leading-relaxed
        [&_b]:font-bold [&_strong]:font-bold [&_i]:italic [&_em]:italic
        [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1
        [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1
        [&_li]:my-0.5
        [&_p]:my-1 ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
